import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * A robust, centered tutorial component for guiding users through an iframe's UI.
 * @param {object} props
 * @param {React.RefObject<HTMLIFrameElement>} props.iframeRef - A ref to the iframe element.
 * @param {Array<object>} props.steps - An array of tutorial step configurations.
 * @param {Function} props.onFinish - A callback function to execute when the tutorial is finished or skipped.
 */
const ReaderTutorial = ({ iframeRef, steps, onFinish }) => {
  // --- Hooks (Called Unconditionally) ---
  const [showPrompt, setShowPrompt] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [arrowStyle, setArrowStyle] = useState({});

  const hasSteps = Array.isArray(steps) && steps.length > 0;
  const currentStep = hasSteps ? steps[currentStepIndex] : null;

  // This effect handles the case where no steps are provided.
  useEffect(() => {
    if (!hasSteps) {
      if (onFinish) onFinish();
    }
  }, [hasSteps, onFinish]);

  // This effect calculates the arrow's rotation and length from the screen's center.
  useLayoutEffect(() => {
    // Guard clause inside the hook ensures it only runs when active.
    if (!isActive || !currentStep || !iframeRef.current) {
      setArrowStyle({});
      return;
    }

    const iframe = iframeRef.current;
    const iframeDoc = iframe.contentDocument;

    const calculateArrowPosition = () => {
      if (!iframeDoc) return;

      const iframeRect = iframe.getBoundingClientRect();
      let targetRect;

      // --- Get Target Coordinates ---
      if (currentStep.targetId) {
        const element = iframeDoc.querySelector(`#${currentStep.targetId}`);
        if (element.style.display !== 'none') {
          targetRect = element.getBoundingClientRect();
        } else {
          console.warn(
            `Tutorial target with id "${currentStep.targetId}" not found.`
          );
          setArrowStyle({ display: 'none' });
          return;
        }
      } else if (currentStep.targetPosition) {
        const [y, x] = currentStep.targetPosition.split('-');
        let top = 0,
          left = 0;
        const padding = 30;

        if (y === 'top') top = iframeRect.top + padding;
        if (y === 'middle') top = iframeRect.top + iframeRect.height / 2;
        if (y === 'bottom') top = iframeRect.top + iframeRect.height - padding;

        if (x === 'left') left = iframeRect.left + padding;
        if (x === 'center') left = iframeRect.left + iframeRect.width / 2;
        if (x === 'right') left = iframeRect.left + iframeRect.width - padding;

        targetRect = { top, left, width: 0, height: 0 };
      }

      if (!targetRect) return;

      // --- Calculate Angle and Distance ---
      const screenCenterX = window.innerWidth / 2;
      const screenCenterY = window.innerHeight / 2;

      const targetGlobalX =
        (currentStep.targetId ? iframeRect.left : 0) +
        targetRect.left +
        targetRect.width / 2;
      const targetGlobalY =
        (currentStep.targetId ? iframeRect.top : 0) +
        targetRect.top +
        targetRect.height / 2;

      const deltaX = targetGlobalX - screenCenterX;
      const deltaY = targetGlobalY - screenCenterY;

      const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI) - 90;
      const distance = Math.sqrt(deltaX ** 2 + deltaY ** 2);
      const arrowLength = distance * 0.99;

      setArrowStyle({
        display: 'block',
        height: `${arrowLength > 0 ? arrowLength : 0}px`,
        transform: `rotate(${angle}deg)`,
      });
    };

    if (iframeDoc.readyState === 'complete') {
      const timer = setTimeout(calculateArrowPosition, 100);
      window.addEventListener('resize', calculateArrowPosition);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', calculateArrowPosition);
      };
    } else {
      const loadListener = () => {
        const timer = setTimeout(calculateArrowPosition, 100);
        window.addEventListener('resize', calculateArrowPosition);
        return () => clearTimeout(timer);
      };
      iframe.addEventListener('load', loadListener);
      return () => {
        iframe.removeEventListener('load', loadListener);
        window.removeEventListener('resize', calculateArrowPosition);
      };
    }
  }, [currentStepIndex, isActive, iframeRef, currentStep]);

  // --- Handlers ---
  const handleStart = () => {
    setShowPrompt(false);
    setIsActive(true);
  };
  const handleSkip = () => {
    setShowPrompt(false);
    setIsActive(false);
    if (onFinish) onFinish();
  };
  const handleNext = () => {
    if (currentStepIndex < steps.length - 1)
      setCurrentStepIndex(currentStepIndex + 1);
    else handleSkip();
  };
  const handlePrev = () => {
    if (currentStepIndex > 0) setCurrentStepIndex(currentStepIndex - 1);
  };

  // --- Render Logic ---
  // Early return is now after all hooks have been called.
  if (!hasSteps) {
    return null;
  }

  if (showPrompt) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10000] ">
        <div className="bg-white rounded-lg shadow-xl p-6 md:p-8 text-center w-11/12 max-w-md">
          <h3 className="text-2xl font-bold text-gray-800 mb-3">Jai Bhim!</h3>
          <p className="text-gray-600 mb-6">
            First time on this device? <br />
            Need a quick tour of the book reader controls?
          </p>
          <div className="flex justify-end gap-4">
            <button
              onClick={handleSkip}
              className="px-4 py-2 rounded-md text-gray-700 bg-gray-200 hover:bg-gray-300 font-semibold transition-colors"
            >
              No, thanks
            </button>
            <button
              onClick={handleStart}
              className="px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 font-semibold transition-colors"
            >
              Yes, please!
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 bg-black/30 z-[10000] flex items-center justify-center p-4 pointer-events-none">
      <div
        className="absolute top-1/2 left-1/2"
        style={{
          transformOrigin: 'top center',
          transition: 'transform 0.4s ease, height 0.4s ease',
          ...arrowStyle,
        }}
      >
        <div
          className="w-1.5 h-full bg-white/90 rounded-full mx-auto"
          style={{ boxShadow: '0 0 15px rgba(0,0,0,0.3)' }}
        ></div>
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0"
          style={{
            borderLeft: '12px solid transparent',
            borderRight: '12px solid transparent',
            borderTop: '22px solid white',
            filter: 'drop-shadow(0 3px 4px rgba(0,0,0,0.5))',
          }}
        ></div>
      </div>
      <div className="relative bg-white text-gray-800 p-5 rounded-lg shadow-2xl w-full max-w-sm text-center pointer-events-auto">
        <p className="text-base leading-relaxed mb-5">{currentStep.text}</p>
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-500">
            {currentStepIndex + 1} / {steps.length}
          </span>
          <div className="flex items-center gap-3">
            {currentStepIndex > 0 && (
              <button
                onClick={handlePrev}
                className="px-4 py-2 rounded text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 font-semibold transition-colors"
              >
                Prev
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-5 py-2 rounded text-sm text-white bg-blue-600 hover:bg-blue-700 font-semibold transition-colors"
            >
              {currentStepIndex === steps.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReaderTutorial;
